import { __ } from '../../utilities/common';
import { rem } from 'polished';
import Button from '@sportnet/ui/Button';
import Checkbox from '@sportnet/ui/Checkbox';
import Input from '@sportnet/ui/Input';
import Modal, { ModalActions, ModalContent } from '@sportnet/ui/Modal';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Label = styled.div`
  font-size: ${rem(14)};
  margin-bottom: ${rem(8)};
`;

const PublicSection = styled.div`
  margin-top: ${rem(20)};
  padding-top: ${rem(15)};
  border-top: ${rem(1)} solid #eee;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: ${rem(14)};
  > span {
    margin-left: ${rem(10)};
  }
`;

const UrlRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${rem(15)};
  > *:first-child {
    flex: 1 0 0;
    margin-right: ${rem(10)};
  }
`;

const Hint = styled.div`
  margin-top: ${rem(10)};
  font-size: ${rem(12)};
  color: #888;
`;

const buildPublicUrl = (appId, appSpace, folderId) => {
  const base = process.env.REACT_APP_API_BASE_URL || '';
  return `${base}/public/app/${encodeURIComponent(
    appId,
  )}/space/${encodeURIComponent(appSpace)}/files?folderId=${encodeURIComponent(
    folderId,
  )}`;
};

const FolderEditModal = ({
  folder,
  appId,
  appSpace,
  onClose = () => {},
  onSave = () => {},
}) => {
  const [name, setName] = useState(folder.title || '');
  const [publicListing, setPublicListing] = useState(!!folder.publicListing);
  const [copied, setCopied] = useState(false);
  const nameRef = useRef();

  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  const publicUrl = buildPublicUrl(appId, appSpace, folder._id);
  const canSave = name.trim().length > 0;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard nemusí byť dostupný – používateľ URL skopíruje ručne
    }
  };

  const handleSave = () => {
    if (!canSave) {
      return;
    }
    onSave({
      name: name.trim(),
      // verejný listing riešime len pre appSpace adresáre
      publicListing: appSpace ? publicListing : undefined,
    });
  };

  return (
    <Modal isOpen handleClose={onClose}>
      <ModalContent>
        <Label>{__('Názov adresára')}</Label>
        <Input
          inputRef={nameRef}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
        />

        {appSpace && (
          <PublicSection>
            <CheckboxLabel>
              <Checkbox
                checked={publicListing}
                onChange={(ev) => setPublicListing(ev.target.checked)}
              />
              <span>{__('Zverejniť obsah adresára (verejný listing)')}</span>
            </CheckboxLabel>
            {publicListing && (
              <>
                <UrlRow>
                  <Input readOnly value={publicUrl} />
                  <Button basic type="button" onClick={handleCopy}>
                    {copied ? __('Skopírované') : __('Kopírovať')}
                  </Button>
                </UrlRow>
                <Hint>
                  {__(
                    'Na tejto adrese je priamy obsah adresára verejne (bez prihlásenia) dostupný. Vracia len platné (neexpirované) súbory.',
                  )}
                </Hint>
              </>
            )}
          </PublicSection>
        )}
      </ModalContent>
      <ModalActions>
        <Button basic onClick={onClose}>
          {__('Zrušiť')}
        </Button>
        <Button basic primary disabled={!canSave} onClick={handleSave}>
          {__('Uložiť')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

FolderEditModal.propTypes = {
  folder: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    publicListing: PropTypes.bool,
  }).isRequired,
  appId: PropTypes.string,
  appSpace: PropTypes.string,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

FolderEditModal.defaultProps = {
  appId: undefined,
  appSpace: undefined,
  onClose: () => {},
  onSave: () => {},
};

export default FolderEditModal;
